/**
 * PosRing — proportional green/red indicator for per-user activity on a
 * market row. Shape signals the *kind* of activity, color split signals
 * the YES/NO breakdown:
 *
 *   variant='closed'  →  solid pie wedges (existing contracts)
 *   variant='open'    →  outline ring with colored arcs (working orders)
 *
 * Previous incarnation embedded a 'C' / 'O' glyph in the center; the
 * letters were unreadable at table scale, so we lean on shape alone.
 * Used by GameTable + SpreadsTotalsTable so the two views share one
 * visual language.
 */

interface PosRingProps {
  yesCount: number;
  noCount: number;
  variant: 'open' | 'closed';
  /** Pixel diameter of the ring. Geometry scales proportionally. */
  size?: number;
}

const VIEWBOX = 14;
const CX = 7;
const CY = 7;
const R = 6;

/** SVG arc path: pie wedge from `startAngle` to `endAngle` (radians, 0 at
 *  12 o'clock, clockwise positive). Used for the solid 'closed' variant. */
function wedgePath(startAngle: number, endAngle: number): string {
  const startX = CX + R * Math.sin(startAngle);
  const startY = CY - R * Math.cos(startAngle);
  const endX = CX + R * Math.sin(endAngle);
  const endY = CY - R * Math.cos(endAngle);
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
  return `M ${CX} ${CY} L ${startX} ${startY} A ${R} ${R} 0 ${largeArc} 1 ${endX} ${endY} Z`;
}

export function PosRing({ yesCount, noCount, variant, size = 14 }: PosRingProps) {
  const total = yesCount + noCount;
  if (total <= 0) return null;
  const yesFraction = yesCount / total;
  const yesOnly = noCount === 0;
  const noOnly = yesCount === 0;

  if (variant === 'closed') {
    // Solid pie — one full circle when single-sided, two wedges when mixed.
    return (
      <svg width={size} height={size} viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`} className="shrink-0">
        {yesOnly && <circle cx={CX} cy={CY} r={R} className="fill-positive" />}
        {noOnly && <circle cx={CX} cy={CY} r={R} className="fill-negative" />}
        {!yesOnly && !noOnly && (
          <>
            <path d={wedgePath(0, 2 * Math.PI * yesFraction)} className="fill-positive" />
            <path d={wedgePath(2 * Math.PI * yesFraction, 2 * Math.PI)} className="fill-negative" />
          </>
        )}
      </svg>
    );
  }

  // Open: outline ring with colored arcs proportional to yes/no counts.
  const circumference = 2 * Math.PI * R;
  const yesLen = circumference * yesFraction;
  const noLen = circumference - yesLen;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`} className="shrink-0">
      <g transform={`rotate(-90 ${CX} ${CY})`}>
        {yesLen > 0 && (
          <circle
            cx={CX}
            cy={CY}
            r={R}
            fill="none"
            strokeWidth={1.5}
            className="stroke-positive"
            strokeDasharray={`${yesLen} ${circumference}`}
            strokeDashoffset={0}
          />
        )}
        {noLen > 0 && (
          <circle
            cx={CX}
            cy={CY}
            r={R}
            fill="none"
            strokeWidth={1.5}
            className="stroke-negative"
            strokeDasharray={`${noLen} ${circumference}`}
            strokeDashoffset={-yesLen}
          />
        )}
      </g>
    </svg>
  );
}
